import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatDate } from '../../../utils';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import inquiryService from '../../../services/inquiryService';

const MessageCenter = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [messages, setMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  // Load user's inquiries/messages from Supabase
  useEffect(() => {
    const loadMessages = async () => {
      if (!user?.id) return;

      setIsLoading(true);
      try {
        // Fetch user's inquiries
        const result = await inquiryService.getUserInquiries(user.id);

        if (result.success && result.data) {
          // Transform inquiries into message format
          const formattedMessages = result.data.map(inquiry => ({
            id: inquiry.id,
            vendorId: inquiry.vendor_id,
            vendorName: inquiry.vendor_profiles?.business_name || 'Unknown Vendor',
            category: inquiry.vendor_profiles?.category || 'General',
            lastMessage: inquiry.message || inquiry.subject || 'No message',
            timestamp: inquiry.created_at,
            unread: inquiry.status === 'pending',
            avatar: inquiry.vendor_profiles?.profile_image || `https://ui-avatars.com/api/?name=${inquiry.vendor_profiles?.business_name || 'V'}&background=8B1538&color=fff`,
            status: inquiry.status || 'active'
          }));

          setMessages(formattedMessages);
        }
      } catch (error) {
        console.error('Error loading messages:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadMessages();
  }, [user]);

  const getFilteredMessages = () => {
    switch (activeTab) {
      case 'unread':
        return messages.filter(msg => msg.unread);
      case 'vendors':
        return messages.filter(msg => msg.status === 'active');
      default:
        return messages;
    }
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      return formatDate(timestamp);
    }
  };

  const handleOpenMessage = (message) => {
    navigate('/message-detail', {
      state: {
        messageId: message.id,
        vendorId: message.vendorId,
        vendorName: message.vendorName,
        category: message.category
      }
    });
  };

  const handleComposeMessage = () => {
    navigate('/all-vendors');
  };

  const unreadCount = messages.filter(msg => msg.unread).length;

  return (
    <div className="bg-white rounded-xl shadow-soft p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-heading font-semibold text-xl text-text-primary">
            Message Center
          </h3>
          <p className="text-text-secondary text-sm">
            Communication hub for vendor messages
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {unreadCount > 0 && (
            <div className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-medium">
              {unreadCount} new
            </div>
          )}
          <Button
            variant="primary"
            size="sm"
            iconName="Plus"
            onClick={handleComposeMessage}
          >
            Compose
          </Button>
        </div>
      </div>

      {/* Message Tabs */}
      <div className="mb-6">
        <div className="flex space-x-1 bg-surface-100 p-1 rounded-lg">
          {[
            { key: 'all', label: 'All Messages', count: messages.length },
            { key: 'unread', label: 'Unread', count: unreadCount },
            { key: 'vendors', label: 'Vendors', count: messages.filter(m => m.status === 'active').length }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${activeTab === tab.key
                ? 'bg-white text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary'
                }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {/* Message List */}
      <div className="space-y-2">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-text-secondary">Loading messages...</p>
          </div>
        ) : getFilteredMessages().length > 0 ? (
          getFilteredMessages().map((message) => (
            <div
              key={message.id}
              onClick={() => handleOpenMessage(message)}
              className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-soft ${message.unread
                ? 'bg-primary-50 border-primary-200' : 'bg-surface-50 border-border-light hover:bg-surface-100'
                }`}
            >
              <div className="flex items-start space-x-3">
                <div className="relative">
                  <img
                    src={message.avatar}
                    alt={message.vendorName}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  {message.unread && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white"></div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <h4 className={`font-medium ${message.unread ? 'text-primary' : 'text-text-primary'}`}>
                        {message.vendorName}
                      </h4>
                      <p className="text-sm text-text-secondary">{message.category}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-text-muted">
                        {formatMessageTime(message.timestamp)}
                      </div>
                    </div>
                  </div>

                  <p className={`text-sm line-clamp-2 ${message.unread ? 'text-text-primary font-medium' : 'text-text-secondary'
                    }`}>
                    {message.lastMessage}
                  </p>

                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="MessageCircle" size={14} className="text-text-muted" />
                      <span className="text-xs text-text-muted">Vendor message</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="ChevronRight" size={14} className="text-text-muted" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-surface-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="MessageSquare" size={32} className="text-text-muted" />
            </div>
            <h4 className="font-medium text-text-primary mb-2">No messages yet</h4>
            <p className="text-text-secondary mb-6">
              {activeTab === 'all' ? 'Your vendor conversations will appear here'
                : `No ${activeTab} messages found`}
            </p>
            <Button
              variant="primary"
              size="sm"
              iconName="Search"
              onClick={() => navigate('/all-vendors')}
            >
              Browse Vendors
            </Button>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      {messages.length > 0 && (
        <div className="mt-6 pt-4 border-t border-border-light">
          <div className="flex items-center justify-between">
            <div className="text-sm text-text-muted">
              {messages.length} total conversations
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Archive"
                onClick={() => {
                  // Archive all read messages
                  showToast('Read messages archived successfully', 'success');
                }}
              >
                Archive Read
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Check"
                onClick={() => {
                  // Mark all as read
                  showToast('All messages marked as read', 'success');
                }}
              >
                Mark All Read
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageCenter;